import { ParsedCsvRow, AnalysisParams } from '@/components/user/csv-analysis/types';

export interface LossStreakOptimizationResult {
  optimalMaxLosses: number;
  totalProfit: number;
  maxDrawdown: number;
  profitDDRatio: number;
  winRate: number;
  totalTrades: number;
  skippedTrades: number;
  effectiveness: number; // Percentage of trades that would be protected
}

export interface LossStreakTestResult extends LossStreakOptimizationResult {
  maxLosses: number;
  status: 'optimal' | 'good' | 'poor';
}

export class LossStreakOptimizer {
  /**
   * Find optimal loss streak protection settings with anti look-ahead bias
   */
  static findOptimalLossStreak(
    csvData: ParsedCsvRow[],
    maxLossesToTest: number,
    params: AnalysisParams
  ): LossStreakOptimizationResult {
    const results = this.findTopLossStreakSettings(csvData, maxLossesToTest, params);
    return results.length > 0 ? results[0] : {
      optimalMaxLosses: 1,
      totalProfit: 0,
      maxDrawdown: 0,
      profitDDRatio: 0,
      winRate: 0,
      totalTrades: 0,
      skippedTrades: 0,
      effectiveness: 0
    };
  }

  /**
   * Find top 10 loss streak settings with status classification
   */
  static findTopLossStreakSettings(
    csvData: ParsedCsvRow[],
    maxLossesToTest: number,
    params: AnalysisParams
  ): LossStreakTestResult[] {
    // Sort data chronologically to ensure forward-looking analysis
    const sortedData = [...csvData].sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      return dateA - dateB;
    });

    const results: LossStreakTestResult[] = [];

    // Test different max loss values
    for (let maxLosses = 1; maxLosses <= maxLossesToTest; maxLosses++) {
      const result = this.testLossStreakSetting(sortedData, maxLosses, params);
      
      // Classify status based on performance
      let status: 'optimal' | 'good' | 'poor' = 'poor';
      if (result.profitDDRatio >= 3.0 && result.effectiveness >= 30) {
        status = 'optimal';
      } else if (result.profitDDRatio >= 2.0 || result.effectiveness >= 20) {
        status = 'good';
      }
      
      results.push({ 
        ...result, 
        optimalMaxLosses: maxLosses,
        maxLosses,
        status
      });
    }

    // Sort by profit/DD ratio (desc), then effectiveness (desc), then profit (desc)
    return results.sort((a, b) => {
      if (b.profitDDRatio !== a.profitDDRatio) {
        return b.profitDDRatio - a.profitDDRatio;
      }
      if (b.effectiveness !== a.effectiveness) {
        return b.effectiveness - a.effectiveness;
      }
      return b.totalProfit - a.totalProfit;
    }).slice(0, 10);
  }

  /**
   * Test a specific loss streak setting using time-series validation
   */
  private static testLossStreakSetting(
    csvData: ParsedCsvRow[],
    maxLosses: number,
    params: AnalysisParams
  ): LossStreakOptimizationResult {
    console.log(`🔍 Testing maxLosses=${maxLosses} with ${csvData.length} trades`);
    
    // Apply loss streak rule with ALWAYS resume_after_win: true to avoid look-ahead bias
    const processedData = this.applyLossStreakRule(csvData, maxLosses, true);
    
    console.log(`  - Original trades: ${csvData.length}`);
    console.log(`  - Active trades: ${processedData.filter(t => !t.isSkipped).length}`);
    console.log(`  - Skipped trades: ${processedData.filter(t => t.isSkipped).length}`);

    // Calculate statistics using normalized PnL
    const activeTrades = processedData.filter(trade => !trade.isSkipped);
    const skippedTrades = processedData.filter(trade => trade.isSkipped);
    
    // Normalize P&L based on TP/SL and quantity
    const normalizedTrades = activeTrades.map(trade => {
      let normalizedPnl = trade.pnl;
      
      if (params.tp_usd > 0 || params.sl_usd > 0) {
        const pq = params.quantity_base > 0 ? (trade.position_size / params.quantity_base) : 1;
        
        if (trade.pnl > 0 && params.tp_usd > 0) {
          normalizedPnl = params.tp_usd * pq;
        } else if (trade.pnl < 0 && params.sl_usd > 0) {
          normalizedPnl = -params.sl_usd * pq;
        }
      }
      
      return { ...trade, normalized_pnl: normalizedPnl };
    });
    
    const winningTrades = normalizedTrades.filter(trade => trade.normalized_pnl > 0);
    const totalProfit = normalizedTrades.reduce((sum, trade) => sum + trade.normalized_pnl, 0);
    const winRate = normalizedTrades.length > 0 ? (winningTrades.length / normalizedTrades.length) * 100 : 0;

    // Calculate max drawdown
    let maxDrawdown = 0;
    let peak = params.initial_capital || 0;
    let runningPnl = 0;
    
    for (const trade of normalizedTrades) {
      runningPnl += trade.normalized_pnl;
      const currentEquity = (params.initial_capital || 0) + runningPnl;
      peak = Math.max(peak, currentEquity);
      const drawdown = peak - currentEquity;
      maxDrawdown = Math.max(maxDrawdown, drawdown);
    }

    const profitDDRatio = maxDrawdown > 0 ? totalProfit / maxDrawdown : totalProfit > 0 ? Infinity : 0;
    const effectiveness = csvData.length > 0 ? (skippedTrades.length / csvData.length) * 100 : 0;

    return {
      optimalMaxLosses: maxLosses,
      totalProfit,
      maxDrawdown,
      profitDDRatio,
      winRate,
      totalTrades: normalizedTrades.length,
      skippedTrades: skippedTrades.length,
      effectiveness,
    };
  }

  /**
   * Apply loss streak rule with forward-looking validation
   * IMPORTANT: This must match exactly with CsvAnalysisPanel.tsx logic
   */
  private static applyLossStreakRule(
    trades: ParsedCsvRow[], 
    maxLosses: number, 
    resumeAfterWin: boolean
  ): ParsedCsvRow[] {
    const result: ParsedCsvRow[] = [];
    let consecutiveLosses = 0;
    let isPaused = false;
    
    for (const trade of trades) {
      const isWin = trade.pnl > 0;
      const isLoss = trade.pnl < 0;
      
      if (isPaused) {
        if (isWin) {
          // End pause state
          isPaused = false;
          consecutiveLosses = 0;
          
          if (resumeAfterWin) {
            // Skip the winning trade that ends the pause
            result.push({
              ...trade,
              isSkipped: true,
              skipReason: 'Resume after win'
            });
          } else {
            // Include the winning trade that ends the pause
            result.push({
              ...trade,
              isSkipped: false
            });
          }
        } else {
          // Still paused, skip all trades
          result.push({
            ...trade,
            isSkipped: true,
            skipReason: `Paused after ${maxLosses} consecutive losses`
          });
        }
      } else {
        // Not paused, include trade
        result.push({
          ...trade,
          isSkipped: false
        });
        
        // Update loss streak counter
        if (isLoss) {
          consecutiveLosses++;
          if (consecutiveLosses >= maxLosses) {
            isPaused = true;
          }
        } else if (isWin) {
          consecutiveLosses = 0;
        }
      }
    }
    
    return result;
  }
}